export const PENDING_STATES = [
  'pendiente_activacion',
  'pendiente_sede',
  'pendiente_pago_online',
  'pendiente_habilitacion',
  'pendiente',
];

import { getUserTrainerId } from './branding';
import { todayCycleStr } from './cycleUtils';
import { isSedeFinalUser } from './gymRoleScope';
import { isFutbolOnlyConsumer } from './futbolModule';
import { isFitLiveUiEnabled } from './fitLiveModule';

/** Roles operador/admin: no son consumidores de vigencia comercial. */
const NON_CONSUMER_ROLES = new Set([
  'super_admin',
  'admin_marca',
  'admin_gym_virtual',
  'admin_gimnasio',
  'admin_gym',
  'admin_sede',
  'admin_d28d',
  'admin_food_plan',
  'admin_food',
  'admin_training',
  'admin_entrenador',
  'entrenador',
  'entrenador_d28d',
  'entrenador_gym',
  'nutricionista',
]);

function isOperatorOrStaffUser(user) {
  const primary = user?.rol || '';
  const extra = Array.isArray(user?.roles) ? user.roles : [];
  return [...new Set([primary, ...extra].filter(Boolean))]
    .some((r) => NON_CONSUMER_ROLES.has(r));
}

export function isPendingPayment(estado) {
  return PENDING_STATES.includes(String(estado || '').toLowerCase());
}

export function hasActiveConsumerLicense(user) {
  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  return licenses.some((lic) => lic.active !== false);
}

export function isSubscriptionActive(subscription) {
  if (!subscription?.has_account) return false;
  return subscription.vigencia_activa === true
    || String(subscription.estado || '').toLowerCase() === 'activo';
}

function hasCommercialModuleIntent(user) {
  const access = user?.module_access || {};
  return Boolean(
    access.d28d || access.gym || access.live_classes || access.training || access.futbol || access.food_plan || access.nutrition,
  );
}

/** Cliente de entrenador independiente sin pago/activación del coach. */
export function isCoachClientAccessBlocked(user) {
  if (!getUserTrainerId(user)) return false;
  if (isCoachClientSubscriptionActive(user)) return false;
  return true;
}

/** Suscripción comercial activa para cliente de coach (coach pagó / activó vía Wompi). */
export function isCoachClientSubscriptionActive(user) {
  if (!getUserTrainerId(user)) return false;
  const sub = user?.subscription || {};
  const estado = String(sub.estado || '').toLowerCase();
  if (sub.pending || isPendingPayment(estado)) return false;
  if (sub.has_account && (estado === 'activo' || estado === 'revisado')
    && (sub.vigencia_activa === true || sub.active === true || estado === 'revisado')) {
    return true;
  }
  // Licencia Training vigente (cuenta aún no realineada): no bloquear al consumidor.
  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  const now = Date.now();
  return licenses.some((lic) => {
    if (String(lic?.module_code || '').toLowerCase() !== 'training') return false;
    if (lic.active === false) return false;
    if (!lic.valid_until) return true;
    const until = new Date(lic.valid_until).getTime();
    return Number.isFinite(until) && until >= now;
  });
}

export function isCoachClientPendingActivation(user) {
  if (!getUserTrainerId(user)) return false;
  return isCoachClientAccessBlocked(user);
}

/** Consumidor sede: vigencia + activación admin; nunca diferido por ciclo D28D. */
export function isSedeVigenciaConsumer(user) {
  if (isOperatorOrStaffUser(user)) return false;
  const trainerId = getUserTrainerId(user);
  const gymId = user?.gym_id ?? user?.gymId ?? null;
  if (trainerId != null && gymId == null) return false;
  return isSedeFinalUser(user) || user?.is_sede_final === true;
}

/** Inscripción a ciclo futuro (pago/confirmación OK; espera enable = start − 1d). */
export function isDeferredCycleEnrollment(user) {
  // usuario_final_sede: no aplica ciclos / habilitación un día antes.
  if (isSedeVigenciaConsumer(user)) return false;
  // Cliente Training (coach): no usa ciclos D28D — un cycle_id contaminado no debe bloquear.
  if (getUserTrainerId(user)) {
    const kind = String(user?.subscription?.kind || user?.subscription?.plan_kind || '').toLowerCase();
    const ma = user?.module_access || {};
    if (kind === 'training' || (ma.training === true && !ma.d28d && !ma.live_classes)) {
      return false;
    }
  }
  const sub = user?.subscription || {};
  const estado = String(sub.estado || '').toLowerCase();
  const hasMeta = Boolean(
    sub.cycle_id
    || sub.cycle_start_date
    || sub.cycle_deferred_until
    || sub.cycle_enable_date,
  );
  if (!hasMeta) return false;
  if (estado === 'pendiente_habilitacion') return true;
  // Restaurar aviso de fecha de inicio si el ciclo inscrito aún no arranca
  // (aunque el estado haya quedado activo por un force previo).
  const start = sub.cycle_start_date ? String(sub.cycle_start_date).slice(0, 10) : null;
  const enable = deferredEnableDateKey(sub);
  const today = todayCycleStr();
  if (start && start > today) return true;
  if (enable && enable > today) return true;
  return false;
}

function deferredEnableDateKey(sub) {
  if (sub?.cycle_enable_date) return String(sub.cycle_enable_date).slice(0, 10);
  if (sub?.cycle_deferred_until) return String(sub.cycle_deferred_until).slice(0, 10);
  if (sub?.cycle_start_date) {
    const start = String(sub.cycle_start_date).slice(0, 10);
    const d = new Date(`${start}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    }
  }
  return null;
}

export function isConsumerAccessBlocked(user) {
  // sede_branch_active NO desbloquea: cada consumidor de sede requiere cuenta activa
  // (activación por admin_sede).

  // Operadores (admin_sede, gym, coaches, etc.): no aplicar bloqueo de consumidor.
  if (isOperatorOrStaffUser(user)) return false;

  // futbol.app es producto independiente: no aplica vigencia/activación D28D ni gym.
  if (isFutbolOnlyConsumer(user)) return false;

  if (isCoachClientAccessBlocked(user)) return true;

  // Fit Live (flag on): marketplace independiente — no bloquear por ciclo/vigencia D28D.
  if (isFitLiveUiEnabled()) return false;

  const sub = user?.subscription || {};
  const estado = String(sub.estado || '').toLowerCase();
  const today = todayCycleStr();
  const sedeVigencia = isSedeVigenciaConsumer(user);

  // Sede: pendiente_habilitacion por ciclo no aplica — tratar como pendiente de activación admin.
  if (!sedeVigencia && estado === 'pendiente_habilitacion') {
    const enableKey = deferredEnableDateKey(sub);
    if (enableKey && enableKey <= today) return false;
    return true;
  }

  // Ciclo futuro con meta de diferido: bloquear y mostrar mensaje de fecha de inicio
  // (cubre cuentas que quedaron «activo» al confirmar pago de un ciclo posterior).
  // Sede: isDeferredCycleEnrollment ya es false.
  if (isDeferredCycleEnrollment(user) && estado === 'activo') {
    const enableKey = deferredEnableDateKey(sub);
    const start = sub.cycle_start_date ? String(sub.cycle_start_date).slice(0, 10) : null;
    if ((enableKey && enableKey > today) || (start && start > today)) {
      return true;
    }
  }

  if (sub.pending || isPendingPayment(sub.estado)) return true;

  if (['vencido', 'cancelado', 'rechazado'].includes(estado)) return true;

  // ACTIVO con vigencia: acceso normal. El diferido por ciclo solo aplica a
  // pendiente_habilitacion (rama arriba). No bloquear si admin ya dejó ACTIVO
  // aunque cycle_deferred_until siga en el resumen (inconsistencia admin vs UI).
  if (sub.has_account && (sub.vigencia_activa || sub.active || estado === 'activo')) {
    return false;
  }

  if (sub.has_account && !isSubscriptionActive(sub)) return true;

  if (!sub.has_account) {
    if (user?.is_sede_final || user?.gym_parent_id != null) return true;
    if (hasActiveConsumerLicense(user)) return false;
    if (hasCommercialModuleIntent(user)) return true;
    return !hasActiveConsumerLicense(user);
  }

  return false;
}

export function pendingPaymentMessage(t, user = null) {
  if (isCoachClientPendingActivation(user)) {
    return t(
      'account.pending_coach_client',
      'Tu entrenamiento está pendiente de activación. Tu coach debe pagar vía Wompi desde «Activar clientes» antes de que puedas usar el perfil.',
    );
  }
  // usuario_final_sede: bienvenida a clases virtuales + pendiente activación (sin ciclos).
  if (isSedeVigenciaConsumer(user)) {
    return t(
      'account.pending_sede_welcome',
      'Bienvenido/a a las clases virtuales de D28D. Tu cuenta está pendiente de activación. Cualquier duda, contacta a soporte.',
    );
  }
  // Food-only (plan FOOD / kind food): no mencionar admin D28D/gimnasio.
  // No importar userServices (ciclo); misma exclusión de licencias D28D/Training activas.
  const activeCodes = new Set(
    (Array.isArray(user?.licenses) ? user.licenses : [])
      .filter((lic) => lic.active !== false)
      .map((lic) => String(lic.module_code || '').toLowerCase()),
  );
  const hasOtherProduct = ['d28d', 'gym', 'live_classes', 'training'].some((c) => activeCodes.has(c));
  if (!hasOtherProduct && (activeCodes.has('futbol') || user?.module_access?.futbol === true)) {
    return t(
      'account.pending_futbol',
      'Tu escuela de fútbol está pendiente de activación. Un administrador de futbol.app o de tu escuela debe habilitarlo.',
    );
  }
  const planUpper = String(user?.subscription?.plan || user?.planId || user?.plan || '').trim().toUpperCase();
  const planKind = String(
    user?.subscription?.kind || user?.subscription?.plan_kind || user?.plan_kind || '',
  ).toLowerCase();
  const looksFoodOnly = !hasOtherProduct && (
    planKind === 'food'
    || planUpper === 'FOOD_PLAN'
    || planUpper.startsWith('FOOD')
  );
  if (looksFoodOnly) {
    return t(
      'account.pending_food_payment',
      'Tu FOOD PLAN está pendiente de activación mientras se valida tu pago. Un administrador lo habilitará y podrás entrar a tu plan de alimentación.',
    );
  }
  const sub = user?.subscription || {};
  if (isDeferredCycleEnrollment(user)) {
    const cycleName = sub.cycle_name || t('account.deferred_cycle_fallback', 'ciclo seleccionado');
    const startRaw = sub.cycle_start_date
      || (sub.cycle_deferred_until
        ? (() => {
          const enable = String(sub.cycle_deferred_until).slice(0, 10);
          const d = new Date(`${enable}T12:00:00`);
          if (Number.isNaN(d.getTime())) return enable;
          d.setDate(d.getDate() + 1);
          return d.toISOString().slice(0, 10);
        })()
        : null);
    let startLabel = startRaw || '';
    if (startRaw) {
      try {
        const d = new Date(`${String(startRaw).slice(0, 10)}T12:00:00`);
        if (!Number.isNaN(d.getTime())) {
          startLabel = d.toLocaleDateString('es-CO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
        }
      } catch {
        /* keep raw */
      }
    }
    return t(
      'account.deferred_cycle_welcome',
      'Bienvenid@ a D28D, tu gimnasio virtual, te has inscrito al {cycle} que inicia el {date} y la plataforma la tendrás habilitada un día antes de la fecha de inicio, cualquier duda escríbenos al soporte de WhatsApp',
      { cycle: cycleName, date: startLabel },
    );
  }
  if (sub.cycle_renewal_pending || (isPendingPayment(sub.estado) && sub.cycle_id)) {
    return t(
      'account.pending_cycle_renewal',
      'Tu ciclo D28D terminó. Renueva tu plan (pago en línea o en sede) para continuar con el siguiente ciclo. Mientras tanto el acceso está restringido.',
    );
  }
  return t(
    'account.pending_payment',
    'Tu servicio está pendiente de activación. Un administrador de D28D o de tu gimnasio debe habilitarlo antes de usar las funciones.',
  );
}
